import { arrayToIdKeyedObject } from '@sequencehq/utils'
import { apiDatesAdapters } from '@sequencehq/utils/dates'
import * as commonEntityLoaders from 'Cube/communication/external/common.api.v1/ports/entityLoaders'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { useCallback } from 'react'
import { amendmentScheduleToCubeData } from './amendmentScheduleToCubeData'
import { scheduleToAmendmentSnapshot } from './scheduleToAmendmentSnapshot'
import { useAddPricingBlock } from './useAddPricingBlock'
import { AmendableSchedule } from './useAmendableSchedules'

export type ScheduleAmendment = {
  schedule: AmendableSchedule
  amendmentDate: Date
}

/**
 * Replaces the quote's phases with the schedule being amended, split at the
 * date the changes take effect. Resolves to whether it was applied, so the
 * modal can stay open for a retry when it wasn't.
 *
 * The schedule carries its prices but not their products, so those are
 * fetched here. They aren't optional - cleanup deletes any price whose product
 * isn't loaded.
 */
export const useApplyAmendmentToQuote = () => {
  const cubeContext = useCubeContext()
  const productsLoader = commonEntityLoaders.useLoadProducts()
  const addPricingBlock = useAddPricingBlock()

  return useCallback(
    async ({
      schedule,
      amendmentDate
    }: ScheduleAmendment): Promise<boolean> => {
      const products = await productsLoader({ prices: schedule.prices })
        .then(result => result.products)
        .catch(() => [])

      // A price without its product gets deleted by cleanup, so carrying on
      // after a failed or partial load would quietly strip prices from the
      // amendment. A failed load comes back empty, so this catches both.
      const isMissingAProduct = schedule.prices.some(
        price => !products.some(product => product.id === price.productId)
      )

      if (isMissingAProduct) {
        return false
      }

      const { amendmentPhaseId, common, phases, discounts, minimums } =
        amendmentScheduleToCubeData({
          schedule,
          amendmentDate: apiDatesAdapters.toApi(amendmentDate)
        })

      // Re-applying reuses the schedule's phase ids, so any the rebuild brings
      // back must be kept, or the save would quietly drop those phases.
      const replacedPhaseIds =
        cubeContext.queries.rawData.data.common.phaseIds.filter(
          phaseId => !common.phaseIds.includes(phaseId)
        )

      cubeContext.mutators.updateData({
        quote: {
          dealType: 'AMENDMENT',
          amendment: {
            billingScheduleId: schedule.id,
            effectiveFrom: amendmentDate,
            original: scheduleToAmendmentSnapshot(schedule)
          },
          defaultDueDateDays: schedule.defaultDueDateDays
        },
        common,
        phases,
        discounts,
        minimums,
        prices: arrayToIdKeyedObject(schedule.prices),
        products: arrayToIdKeyedObject(products)
      })

      // The quote's own phases go after the new ones are in, not before -
      // dropping them first leaves `phaseIds` pointing at phases that are no
      // longer there, and the editor renders in between the two updates.
      cubeContext.mutators.deleteData({
        type: 'phases',
        ids: replacedPhaseIds
      })

      cubeContext.mutators.updateEditor({
        highlightedEntityIds: [amendmentPhaseId]
      })

      // The quote now carries the schedule's pricing, so it needs somewhere to
      // show it. A quote that already has a pricing block keeps the one it has.
      addPricingBlock()

      return true
    },
    [
      addPricingBlock,
      cubeContext.mutators,
      cubeContext.queries.rawData.data.common.phaseIds,
      productsLoader
    ]
  )
}
